import Player from '@vimeo/player';

/**
 * The Video class works in conjunction with the video snippet. It scrapes data
 * from the snippet and instantiates a custom Vimeo player on that data.
 * It handles custom play/pause and mute/unmute buttons
 */
export default class Video {
  /**
   * @param {DOMnode} element top-level node for the section/component
   * @param {DOMnode} video top-level node for the video
   * @param {Class} layout for handling breakpoints
   */
  constructor({ element, video, layout }) {
    const dataEl = video.querySelector('[data-component-data]');
    this.element = element;
    this.layout = layout;
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};

    this.isMuted = true;
    this.isMobile = true;
    this.isPlaying = this.data.autoplay;
    this.hasMultipleVideos =
      !!this.data.videoDesktop && !!this.data.videoMobile;
    this.hasAdaptiveNativeContorls = this?.data?.nativeControls === 'on_play' ? true : false

    this.handlePlay = this.handlePlay.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handleStop = this.handleStop.bind(this);
    this.handleBpChange = this.handleBpChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.videoContainer = video;

    this.playEl = this.data.play?.selector
      ? element.querySelector(this.data.play.selector)
      : video.querySelector('[data-video-play]');
    this.muteEl = this.data.mute?.selector
      ? element.querySelector(this.data.mute.selector)
      : video.querySelector('[data-video-mute]');
    this.stopEl = this.data.reset?.selector
      ? element.querySelector(this.data.reset.selector)
      : video.querySelector('[data-video-reset]');

    this.init();
  }

  handlePlay() {
    this.player.classList.add('started')
    if(this.hasAdaptiveNativeContorls) {
      this.player.controls = true
    }
    if (this.stopEl) {
      [...this.stopEl.children].forEach((child) =>
        child.classList.remove('hidden')
      );
    }
    if (this.isPlaying) {
      this.pauseVideo();
    } else this.playVideo();
    if (this.playEl)
      [...this.playEl.children].forEach((child) =>
        child.classList.toggle('hidden')
      );
  }

  /**
   * Handle keyboard events for video accessibility
   * Allows Space and Enter keys to toggle play/pause
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    if (this.isPlaying) {
      this.pauseVideo();
    } else this.playVideo();
  }

  handleStop() {
    if (this.isPlaying) {
      this.pauseVideo();
      if (this.playEl)
        [...this.playEl.children].forEach((child) =>
          child.classList.toggle('hidden')
        );
    }
    [...this.stopEl.children].forEach((child) => child.classList.add('hidden'));
    this.player.currentTime = 0;
    this.player.load();
    this.player.classList.remove('started')
    if(this.hasAdaptiveNativeContorls) {
      this.player.controls = false
    }
  }

  pauseVideo() {
    this.player.pause();
    this.isPlaying = false;
    this.playEl?.setAttribute('aria-label', this.data.play.playText);
    this.updateVideoAriaLabel();
  }

  playVideo() {
    this.player.play();
    this.isPlaying = true;
    this.playEl?.setAttribute('aria-label', this.data.play.pauseText);
    this.updateVideoAriaLabel();
  }

  handleMute() {
    if (this.isMuted) {
      this.unmuteVideo();
    } else {
      this.muteVideo();
    }

    if (this.muteEl)
      [...this.muteEl.children].forEach((child) =>
        child.classList.toggle('hidden')
      );
  }

  unmuteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = false;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(1);
    }
    this.isMuted = false;
    this.muteEl?.setAttribute('aria-label', this.data.mute.muteText);
  }

  muteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = true;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(0);
    }
    this.isMuted = true;
    this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
  }

  resetPlayer() {
    this.unbindEventListeners();

    if (this.data.host === 'shopify') {
      this.resetShopifyPlayer();
    } else if (this.data.host === 'vimeo') {
      this.resetVimeoPlayer();
    }
  }

  unbindEventListeners() {
    this.playEl?.removeEventListener('click', this.handlePlay);
    this.muteEl?.removeEventListener('click', this.handleMute);
    this.stopEl?.removeEventListener('click', this.handleStop);
    this.videoContainer?.removeEventListener('keydown', this.handleKeydown);
    if (this.layout) {
      window.removeEventListener('bpChange', this.handleBpChange);
      window.removeEventListener('resize', this.handleResize);
    }
  }

  resetShopifyPlayer() {
    this.resetControlButtons();
  }

  resetVimeoPlayer() {
    this.player.destroy().then(() => {
      this.resetControlButtons();
      this.init();
    });
  }

  resetControlButtons() {
    this.isPlaying = this.data.autoplay;
    this.playEl?.setAttribute(
      'aria-label',
      this.data.autoplay ? this.data.play.pauseText : this.data.play.playText
    );
    this.playEl?.children[this.data.autoplay ? 1 : 0]?.classList.remove(
      'hidden'
    );
    this.playEl?.children[this.data.autoplay ? 0 : 1]?.classList.add('hidden');
    this.updateVideoAriaLabel();

    this.isMuted = true;
    this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
    this.muteEl?.children[0]?.classList.remove('hidden');
    this.muteEl?.children[1]?.classList.add('hidden');
  }

  handleBpChange() {
    if (this.isMobile && this.layout.isAboveBreakpoint('lg')) {
      this.isMobile = false;
      this.resetPlayer();
    } else if (!this.isMobile && !this.layout.isAboveBreakpoint('lg')) {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  handleResize() {
    if (this.layout.isAboveBreakpoint('lg')) {
      this.isMobile = false;
    } else {
      this.isMobile = true;
    }
    this.resetPlayer();
    this.init()
  }

  init() {
    if (this.layout && this.hasMultipleVideos) {

      if (this.layout.isAboveBreakpoint('lg')) {
        this.initVideoByType(this.data.videoDesktop);
        this.isMobile = false;
      } else {
        this.initVideoByType(this.data.videoMobile);
        this.isMobile = true;
      }
    } else if (this.data.videoDesktop) {
      this.initVideoByType(this.data.videoDesktop);
    } else if (this.data.videoMobile) {
      this.initVideoByType(this.data.videoMobile);
    }
  }

  initVideoByType(video) {
    if (this.data.host === 'shopify') {
      this.initShopifyVideo(video.id);
    } else if (this.data.host === 'vimeo') {
      this.initVimeoVideo(video);
    }
  }

  calculateVideoHeight() {
    let height = this.element.offsetHeight;
    const width = (height * 16) / 9;
    if (width < this.element.offsetWidth) {
      height *= this.element.offsetWidth / width;
    }
    return height;
  }

  initVimeoVideo(id) {
    this.player = new Player(`video-${id}`, {
      id,
      controls: false,
      autoplay: this.data.autoplay,
      muted: true,
      responsive: true,
      loop: true,
      ...(this.layout.isAboveBreakpoint('lg') &&
        !this.layout.isAboveBreakpoint('xl') &&
        this.data.fullHeight && {
          height: this.calculateVideoHeight(),
          responsive: false,
        }),
    });

    this.bindEventListeners();
  }

  initShopifyVideo(id) {
    this.player = this.element.querySelector(`#video-${id} video`);
    this.bindEventListeners();
    if(this.hasAdaptiveNativeContorls) {
      this.player.controls = false
    }
  }

  bindEventListeners() {
    this.playEl?.addEventListener('click', this.handlePlay);
    this.muteEl?.addEventListener('click', this.handleMute);
    this.stopEl?.addEventListener('click', this.handleStop);

    this.setupKeyboardAccessibility();

    if (this.layout && this.hasMultipleVideos) {
      if (this.data.fullHeight) {
        window.addEventListener('resize', () => {
          if (this.timerId) clearTimeout(this.timerId);
          this.timerId = setTimeout(() => {
            this.handleResize();
          }, 500);
        });
      } else {
        window.addEventListener('bpChange', this.handleBpChange);
      }
    }
  }

  /**
   * Sets up keyboard accessibility for the video
   * Makes the video container focusable and adds keyboard event listeners
   */
  setupKeyboardAccessibility() {
    if (!this.videoContainer) return;

    if (!this.videoContainer.hasAttribute('tabindex')) {
      this.videoContainer.setAttribute('tabindex', '0');
    }

    if (!this.videoContainer.hasAttribute('role')) {
      this.videoContainer.setAttribute('role', 'button');
    }

    this.updateVideoAriaLabel();

    this.videoContainer.addEventListener('keydown', this.handleKeydown);
  }

  updateVideoAriaLabel() {
    if (!this.videoContainer) return;

    const playText = this.data.play?.playText || 'Play video';
    const pauseText = this.data.play?.pauseText || 'Pause video';
    const label = this.isPlaying ? pauseText : playText;

    this.videoContainer.setAttribute('aria-label', label);
  }
}
